import { CoreTypes, type Dash, type Style } from '@vp/shared-types';
import { DEFAULT_TEXT_STYLE } from '@vp/core';
import { toPrimitives, type Geometry, type GeoNode } from '@vp/dskhema';

/**
 * "Explode" a diagram (docs/ru/DSKHEMA.md §7): plan the canvas objects that reproduce a laid-out
 * diagram. The editor creates them in one transaction inside a frame, then glues connectors.
 */
export interface PlannedObject {
  key: string;
  type: string;
  x: number;
  y: number;
  w: number;
  h: number;
  style?: Partial<Style>;
  props?: Record<string, unknown>;
  /** Module object: merge the type's manifest default props under `props`. */
  moduleDefaults?: boolean;
}

export interface PlannedConnector {
  from: string;
  to: string;
  /** Where the link leaves / meets its nodes, relative to their boxes (0..1), as in the scheme. */
  fromAnchor?: { x: number; y: number };
  toAnchor?: { x: number; y: number };
  /** The scheme's own polyline in canvas coordinates (kept until an end moves). */
  points: number[];
  /** Label position as a fraction of the path length. */
  labelAt?: number;
  arrowStart: boolean;
  arrowEnd: boolean;
  label: string;
  style: Partial<Style>;
}

export interface ExplodePlan {
  objects: PlannedObject[];
  connectors: PlannedConnector[];
  /** Route of the created connectors, matching the scheme. */
  route: 'orthogonal' | 'curved' | 'straight';
}

/** Map geometry (diagram coordinates) to canvas objects placed at `box` with scale `s`. */
export function planExplode(g: Geometry, origin: { x: number; y: number }, s: number): ExplodePlan {
  const X = (v: number) => origin.x + v * s;
  const Y = (v: number) => origin.y + v * s;
  const objects: PlannedObject[] = [];
  const connectors: PlannedConnector[] = [];
  const text = (n: { fontSize: number }, color: string, align: 'left' | 'center' = 'center', valign: 'top' | 'middle' = 'middle', bold = false) => ({
    ...DEFAULT_TEXT_STYLE,
    fontSize: n.fontSize * s,
    color,
    align,
    valign,
    bold,
  });
  const stroke = (color: string, width: number, dash: Dash) => ({ color, width: width * s, dash });
  let k = 0;

  const node = (n: GeoNode) => {
    const box = { x: X(n.x), y: Y(n.y), w: n.w * s, h: n.h * s };
    const style: Partial<Style> = { fill: { kind: 'solid', color: n.fill }, stroke: stroke(n.stroke, n.width, n.strokeDash), text: text(n, n.text, 'center', 'middle', !!n.bold) };
    const key = `node:${n.id}`;
    switch (n.shape) {
      case 'rect':
      case 'rounded':
      case 'pill':
      case 'participant':
      case 'actor':
      case 'activation': {
        const radius = n.shape === 'pill' ? n.h / 2 : n.shape === 'rounded' ? 12 : n.shape === 'activation' ? 0 : 4;
        objects.push({ key, type: CoreTypes.rect, ...box, style: { ...style, cornerRadius: radius * s }, props: { text: n.label } });
        return;
      }
      case 'circle':
      case 'ellipse':
        objects.push({ key, type: CoreTypes.ellipse, ...box, style, props: { text: n.label } });
        return;
      case 'diamond':
        objects.push({ key, type: CoreTypes.diamond, ...box, style, props: { text: n.label } });
        return;
      case 'note':
        objects.push({ key, type: CoreTypes.sticker, ...box, style: { fill: { kind: 'solid', color: n.fill }, stroke: null, text: text(n, n.text, 'left', 'middle') }, props: { text: n.label } });
        return;
      case 'triangle':
        objects.push({ key, type: CoreTypes.triangle, ...box, style, props: { text: n.label } });
        return;
      case 'manual':
      case 'data':
      case 'bracket':
      case 'brace':
      case 'display': {
        const left = n.shape === 'bracket' || n.shape === 'brace' || n.shape === 'display';
        objects.push({
          key,
          type: CoreTypes.rect,
          ...box,
          style: {
            fill: n.shape === 'bracket' || n.shape === 'brace' ? { kind: 'none' } : style.fill!,
            stroke: style.stroke,
            text: text(n, n.text, left ? 'left' : 'center', 'middle', !!n.bold),
          },
          props: { text: n.label, form: n.shape, ...(n.flip ? { flip: true } : {}) },
        });
        return;
      }
    }
    if (typeof n.shape === 'string' && n.shape.startsWith('module:')) {
      objects.push({ key, type: n.shape.slice(7), ...box, props: { label: n.label }, moduleDefaults: true });
      return;
    }
    // other forms (hexagon, cylinder, document…): each drawn part becomes an object, the label text on top
    let first = true;
    for (const p of toPrimitives({ kind: 'flow', width: 0, height: 0, items: [n] })) {
      const partKey = first ? key : `${key}:part${k++}`;
      if (p.k === 'path') {
        let x0 = Infinity;
        let y0 = Infinity;
        let x1 = -Infinity;
        let y1 = -Infinity;
        for (let i = 0; i < p.points.length; i += 2) {
          x0 = Math.min(x0, p.points[i]);
          x1 = Math.max(x1, p.points[i]);
          y0 = Math.min(y0, p.points[i + 1]);
          y1 = Math.max(y1, p.points[i + 1]);
        }
        const local: number[] = [];
        for (let i = 0; i < p.points.length; i += 2) local.push((p.points[i] - x0) * s, (p.points[i + 1] - y0) * s);
        const nodes: number[] = [];
        for (let i = 0; i < local.length; i += 2) nodes.push(local[i], local[i + 1], local[i], local[i + 1], local[i], local[i + 1]);
        objects.push({ key: partKey, type: CoreTypes.path, x: X(x0), y: Y(y0), w: Math.max((x1 - x0) * s, 0.01), h: Math.max((y1 - y0) * s, 0.01), style: { fill: p.fill, stroke: p.stroke ? stroke(p.stroke.color, p.stroke.width, p.stroke.dash) : null }, props: { nodes, points: local, closed: p.closed } });
        first = false;
      } else if (p.k === 'shape') {
        objects.push({
          key: partKey,
          type: p.shape === 'ellipse' ? CoreTypes.ellipse : p.shape === 'triangle' ? CoreTypes.triangle : CoreTypes.rect,
          x: X(p.x),
          y: Y(p.y),
          w: p.w * s,
          h: p.h * s,
          style: { fill: p.fill, stroke: p.stroke ? stroke(p.stroke.color, p.stroke.width, p.stroke.dash) : null },
          props: { text: '' },
        });
        first = false;
      }
    }
    if (n.label) {
      const tp = toPrimitives({ kind: 'flow', width: 0, height: 0, items: [n] }).find((p) => p.k === 'text');
      const lb = tp && tp.k === 'text' ? { x: X(tp.x), y: Y(tp.y), w: tp.w * s, h: tp.h * s } : box;
      objects.push({ key: `label:${k++}`, type: CoreTypes.text, ...lb, style: { fill: { kind: 'none' }, stroke: null, text: text(n, n.text) }, props: { text: n.label } });
    }
  };

  for (const it of g.items) {
    switch (it.k) {
      case 'group':
        objects.push({
          key: `group:${k++}`,
          type: CoreTypes.rect,
          x: X(it.x),
          y: Y(it.y),
          w: it.w * s,
          h: it.h * s,
          style: { fill: { kind: 'solid', color: it.fill }, stroke: stroke(it.stroke, 1.2, 'dashed'), cornerRadius: 10 * s, text: text(it, '#475569', 'left', 'top', true) },
          props: { text: it.title },
        });
        break;
      case 'frame': {
        objects.push({
          key: `frame:${k++}`,
          type: CoreTypes.rect,
          x: X(it.x),
          y: Y(it.y),
          w: it.w * s,
          h: it.h * s,
          style: { fill: { kind: 'none' }, stroke: stroke(it.stroke, 1.2, 'solid'), cornerRadius: 4 * s, text: text(it, '#334155', 'left', 'top', true) },
          props: { text: `${it.tag} ${it.label}`.trim() },
        });
        for (const sec of it.sections) {
          objects.push({ key: `sec:${k++}`, type: CoreTypes.line, x: X(it.x), y: Y(sec.y), w: it.w * s, h: 0.01, style: { stroke: stroke(it.stroke, 1, 'dashed') }, props: { points: [0, 0, it.w * s, 0], arrowStart: false, arrowEnd: false, label: sec.label } });
        }
        break;
      }
      case 'node':
        node(it);
        break;
      case 'text':
        objects.push({ key: `text:${k++}`, type: CoreTypes.text, x: X(it.x), y: Y(it.y), w: it.w * s, h: it.h * s, style: { fill: { kind: 'none' }, stroke: null, text: text(it, it.color, it.align === 'left' ? 'left' : 'center') }, props: { text: it.text } });
        break;
      case 'edge': {
        const labelFont = it.label?.fontSize ?? 12;
        const style: Partial<Style> = { stroke: stroke(it.color, it.width, it.dash), text: { ...DEFAULT_TEXT_STYLE, fontSize: labelFont * s, color: it.label?.color ?? '#334155', align: 'center', valign: 'middle' } };
        if (it.from && it.to) {
          const anchor = (id: string, px: number, py: number) => {
            const n = g.items.find((x): x is GeoNode => x.k === 'node' && x.id === id);
            if (!n || it.from === it.to) return undefined;
            const clamp = (v: number) => Math.min(1, Math.max(0, v));
            return { x: clamp((px - n.x) / n.w), y: clamp((py - n.y) / n.h) };
          };
          const pts = it.points;
          const world = pts.map((v, i) => (i % 2 === 0 ? X(v) : Y(v)));
          connectors.push({
            points: world,
            labelAt: it.label ? fractionAt(pts, it.label.x + it.label.w / 2, it.label.y + it.label.h / 2) : undefined,
            fromAnchor: anchor(it.from, pts[0], pts[1]),
            toAnchor: anchor(it.to, pts[pts.length - 2], pts[pts.length - 1]),
            from: `node:${it.from}`,
            to: `node:${it.to}`, arrowStart: it.arrowStart, arrowEnd: it.arrowEnd, label: it.label?.text ?? '', style });
          break;
        }
        // sequence lines keep their exact geometry
        let x0 = Infinity;
        let y0 = Infinity;
        let x1 = -Infinity;
        let y1 = -Infinity;
        for (let i = 0; i < it.points.length; i += 2) {
          x0 = Math.min(x0, it.points[i]);
          x1 = Math.max(x1, it.points[i]);
          y0 = Math.min(y0, it.points[i + 1]);
          y1 = Math.max(y1, it.points[i + 1]);
        }
        const points = it.points.map((v, i) => (i % 2 === 0 ? (v - x0) * s : (v - y0) * s));
        objects.push({
          key: `line:${k++}`,
          type: CoreTypes.line,
          x: X(x0),
          y: Y(y0),
          w: Math.max((x1 - x0) * s, 0.01),
          h: Math.max((y1 - y0) * s, 0.01),
          style,
          props: { points, arrowStart: it.arrowStart, arrowEnd: it.arrowEnd, label: it.label?.text ?? '' },
        });
        break;
      }
    }
  }
  return { objects, connectors, route: g.links === 'curved' ? 'curved' : 'orthogonal' };
}

/** Fraction of a polyline's length at its point closest to (x, y). */
export function fractionAt(p: number[], x: number, y: number): number {
  let total = 0;
  let best = Infinity;
  let at = 0;
  for (let i = 2; i < p.length; i += 2) {
    const ax = p[i - 2];
    const ay = p[i - 1];
    const dx = p[i] - ax;
    const dy = p[i + 1] - ay;
    const len = Math.hypot(dx, dy);
    const t = len ? Math.max(0, Math.min(1, ((x - ax) * dx + (y - ay) * dy) / (len * len))) : 0;
    const d = Math.hypot(ax + dx * t - x, ay + dy * t - y);
    if (d < best) {
      best = d;
      at = total + len * t;
    }
    total += len;
  }
  return total ? at / total : 0.5;
}
